import React, { useLayoutEffect, useRef, useState } from 'react'
import type { TooltipProps, TypographyProps } from '@mui/material'
import { Box, Tooltip, Typography } from '@mui/material'

type NavTruncationTooltipProps = {
  title: string
  children?: React.ReactNode
  placement?: TooltipProps['placement']
  forceTooltip?: boolean
  variant?: TypographyProps['variant']
  component?: TypographyProps['component']
  sx?: TypographyProps['sx']
}

/** Single-line ellipsis; tooltip on hover when the label overflows its container. */
export function NavTruncationTooltip({
  title,
  children,
  placement = 'right',
  forceTooltip = false,
  variant = 'body2',
  component,
  sx
}: NavTruncationTooltipProps) {
  const textRef = useRef<HTMLElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const showTooltip = forceTooltip || isOverflowing
  const noTitle = !title

  const typographySx = {
    m: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    minWidth: 0,
    ...sx
  } as const

  useLayoutEffect(() => {
    if (noTitle || forceTooltip) {
      setIsOverflowing(false)

      return
    }

    const el = textRef.current
    if (!el) {
      return
    }

    const measure = () => {
      const node = textRef.current
      if (!node) {
        return
      }
      setIsOverflowing(node.scrollWidth > node.clientWidth + 1)
    }

    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)

    return () => {
      ro.disconnect()
    }
  }, [title, children, noTitle, forceTooltip])

  const inner = (
    <Box component='span' sx={{ display: 'block', width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <Typography ref={textRef} variant={variant} sx={typographySx} {...(component !== undefined ? { component } : {})}>
        {children ?? title}
      </Typography>
    </Box>
  )

  if (noTitle || !showTooltip) {
    return inner
  }

  return (
    <Tooltip
      title={title}
      placement={placement}
      enterDelay={400}
      componentsProps={{ tooltip: { sx: { maxWidth: 360 } } }}
    >
      {inner}
    </Tooltip>
  )
}
